import appContext from 'context/appContext'
import useApi, { ApiResponse } from 'hooks/useApi'
import useToast from 'hooks/useToast'
import usePermissions, { PermissionOverride } from 'hooks/usePermissions'
import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useLocation } from 'react-router-dom'
import { createCsApiUrlFromEndpoint, getEntityIdFromUrl } from 'utils'
import { FEApiError } from 'utils/errorHandling'
import type { ToastDetails } from 'components/Banner/BannerTypesUtils.d'
import type { ExtractElementType, singleBrandClientV5, singleClientV5 } from 'utils/utils.d'
import type { ProjectsDetailResult } from '@contracts'

export interface ProjectPageContextProps {
  setProjectId: Dispatch<SetStateAction<string | undefined>>
  singleProjectApiDetails: ApiResponse<ProjectsDetailResult | null>
  brandDetails?: singleBrandClientV5
  clientDetails?: singleClientV5
  activeRegion: string | undefined
  setActiveRegion: Dispatch<SetStateAction<string | undefined>>
  updateSingleProject: () => Promise<void>
  toastDetailsContext: ToastDetails | undefined
  setToastDetailsContext: Dispatch<SetStateAction<ToastDetails | undefined>>
  activeRegionData: ExtractElementType<ProjectsDetailResult['regions']> | undefined
  permissions: {
    canGetProject: boolean
  }
}

const initialState: ProjectPageContextProps = {
  setProjectId: () => {},
  singleProjectApiDetails: {
    data: null,
    error: null,
    isLoading: false,
    fetchData: () => Promise.resolve(null),
    url: null!,
    setUrl: function (): void {
      throw new Error('Function not implemented.')
    },
  },
  brandDetails: undefined,
  clientDetails: undefined,
  activeRegion: undefined,
  setActiveRegion: () => {},
  updateSingleProject: async () => {},
  toastDetailsContext: undefined,
  setToastDetailsContext: () => {},
  activeRegionData: undefined,
  permissions: {
    canGetProject: false,
  },
}

export const ProjectPageContext = createContext<ProjectPageContextProps>(initialState)

interface ProjectPageProviderProps {
  children: ReactNode
}

export function ProjectPageProvider({ children }: ProjectPageProviderProps) {
  const [projectId, setProjectId] = useState<string | undefined>(undefined)
  const [activeRegion, setActiveRegion] = useState<string>()
  const { clientsData, osContextValue } = useContext(appContext)
  const { toastDetails: toastDetailsContext, setToastDetails: setToastDetailsContext } = useToast()
  const location = useLocation()

  // get project details
  const singleProjectApiDetails = useApi<ProjectsDetailResult>(`v2/projects/${projectId}`)
  const {
    fetchData: getSingleProject,
    data: getSingleProjectData,
    setUrl: setGetSingleProjectUrl,
  } = useMemo(() => singleProjectApiDetails, [singleProjectApiDetails])

  const [permissionsOverrides, setPermissionsOverrides] = useState<PermissionOverride[]>([])

  const { overridableClientLevelPermissions } = usePermissions({
    clientId: getSingleProjectData?.client_id,
    permissionSubset: ['PDP_APP_GET_V2_PROJECTS_BY'],
    permissionOverrides: permissionsOverrides,
  })

  const [canGetProject] = overridableClientLevelPermissions

  const clientDetails = useMemo(
    () => clientsData?.clients?.find(({ id }) => id === getSingleProjectData?.client_id),
    [clientsData, getSingleProjectData?.client_id],
  )
  const brandDetails = useMemo(
    () => clientDetails?.brands?.find(({ id }) => id === getSingleProjectData?.brand_id),
    [clientDetails, getSingleProjectData?.brand_id],
  )
  const singleProjectDataRegions = useMemo(() => getSingleProjectData?.regions || [], [getSingleProjectData?.regions])
  const activeRegionData = useMemo(
    () => singleProjectDataRegions.find(region => region._id === activeRegion),
    [singleProjectDataRegions, activeRegion],
  )

  // update active region if it's not in the project regions
  const updateSingleProject = useCallback(async () => {
    getSingleProject()
      .then(project => {
        setActiveRegion(currentActiveRegion => {
          // if currentActiveRegion    in the project regions, return it otherwise return the first region
          const doesRegionExist = project.regions?.some(region => region._id === currentActiveRegion)
          return doesRegionExist ? currentActiveRegion : project.regions?.[0]?._id!
        })
      })
      .catch(error => {
        const err = error as FEApiError
        if (err.status === 403) {
          setPermissionsOverrides([
            {
              permission: 'PDP_APP_GET_V2_PROJECTS_BY',
              hasPermission: false,
            },
          ])
        }
      })
  }, [projectId])

  // call initial api
  useEffect(() => {
    if (!projectId) return
    setGetSingleProjectUrl(createCsApiUrlFromEndpoint(`v2/projects/${projectId}`))
    updateSingleProject()
  }, [projectId, updateSingleProject])

  useEffect(() => {
    if (projectId) return
    const projectIdFromUrl = getEntityIdFromUrl(location.pathname, 'project', {
      baseUrl: osContextValue?.osContext?.baseUrl || '',
    })

    if (projectIdFromUrl) setProjectId(projectIdFromUrl)
  }, [])

  return (
    <ProjectPageContext.Provider
      value={{
        setProjectId,
        singleProjectApiDetails,
        brandDetails,
        clientDetails,
        activeRegion,
        setActiveRegion,
        updateSingleProject,
        toastDetailsContext,
        setToastDetailsContext,
        activeRegionData,
        permissions: {
          canGetProject,
        },
      }}
    >
      {children}
    </ProjectPageContext.Provider>
  )
}

export default ProjectPageContext
